/**
 * Currency constants and utilities
 *
 * Provides TypeScript types and hardcoded currency data for multi-currency functionality.
 * Currency configuration is managed in currencies.json in the backend.
 */

/**
 * Supported currencies for multi-currency functionality
 */
export type SupportedCurrency =
    | 'AUD' // Australian Dollar
    | 'BRL' // Brazilian Real
    | 'CAD' // Canadian Dollar
    | 'CHF' // Swiss Franc
    | 'CNY' // Chinese Renminbi Yuan
    | 'CZK' // Czech Koruna
    | 'DKK' // Danish Krone
    | 'EUR' // Euro
    | 'GBP' // British Pound
    | 'HKD' // Hong Kong Dollar
    | 'HUF' // Hungarian Forint
    | 'IDR' // Indonesian Rupiah
    | 'ILS' // Israeli New Shekel
    | 'INR' // Indian Rupee
    | 'ISK' // Icelandic Króna
    | 'JPY' // Japanese Yen
    | 'KRW' // South Korean Won
    | 'MXN' // Mexican Peso
    | 'MYR' // Malaysian Ringgit
    | 'NOK' // Norwegian Krone
    | 'NZD' // New Zealand Dollar
    | 'PHP' // Philippine Peso
    | 'PLN' // Polish Złoty
    | 'RON' // Romanian Leu
    | 'SEK' // Swedish Krona
    | 'SGD' // Singapore Dollar
    | 'THB' // Thai Baht
    | 'TRY' // Turkish Lira
    | 'USD' // United States Dollar
    | 'ZAR'; // South African Rand

export const DEFAULT_CURRENCY: SupportedCurrency = 'USD';

/**
 * Currency definition from backend API
 */
export interface CurrencyDefinition {
    code: string;
    symbol: string;
    name: string;
}

/**
 * Currency configuration from backend API
 */
export interface CurrencyConfiguration {
    currencies: CurrencyDefinition[];
    defaultCurrency: string;
}

/**
 * Fallback currency symbols (used if API is unavailable)
 */
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
    AUD: 'A$',
    BRL: 'R$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    CZK: 'Kč',
    DKK: 'kr',
    EUR: '€',
    GBP: '£',
    HKD: 'HK$',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    INR: '₹',
    ISK: 'kr',
    JPY: '¥',
    KRW: '₩',
    MXN: 'MX$',
    MYR: 'RM',
    NOK: 'kr',
    NZD: 'NZ$',
    PHP: '₱',
    PLN: 'zł',
    RON: 'lei',
    SEK: 'kr',
    SGD: 'S$',
    THB: '฿',
    TRY: '₺',
    USD: '$',
    ZAR: 'R',
};

/**
 * Currency full names mapped to their currency codes
 */
export const CURRENCY_NAMES: Record<SupportedCurrency, string> = {
    AUD: 'Australian Dollar',
    BRL: 'Brazilian Real',
    CAD: 'Canadian Dollar',
    CHF: 'Swiss Franc',
    CNY: 'Chinese Renminbi Yuan',
    CZK: 'Czech Koruna',
    DKK: 'Danish Krone',
    EUR: 'Euro',
    GBP: 'British Pound',
    HKD: 'Hong Kong Dollar',
    HUF: 'Hungarian Forint',
    IDR: 'Indonesian Rupiah',
    ILS: 'Israeli New Shekel',
    INR: 'Indian Rupee',
    ISK: 'Icelandic Króna',
    JPY: 'Japanese Yen',
    KRW: 'South Korean Won',
    MXN: 'Mexican Peso',
    MYR: 'Malaysian Ringgit',
    NOK: 'Norwegian Krone',
    NZD: 'New Zealand Dollar',
    PHP: 'Philippine Peso',
    PLN: 'Polish Złoty',
    RON: 'Romanian Leu',
    SEK: 'Swedish Krona',
    SGD: 'Singapore Dollar',
    THB: 'Thai Baht',
    TRY: 'Turkish Lira',
    USD: 'United States Dollar',
    ZAR: 'South African Rand',
};

/**
 * List of all supported currencies for UI selection
 */
export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
    'AUD',
    'BRL',
    'CAD',
    'CHF',
    'CNY',
    'CZK',
    'DKK',
    'EUR',
    'GBP',
    'HKD',
    'HUF',
    'IDR',
    'ILS',
    'INR',
    'ISK',
    'JPY',
    'KRW',
    'MXN',
    'MYR',
    'NOK',
    'NZD',
    'PHP',
    'PLN',
    'RON',
    'SEK',
    'SGD',
    'THB',
    'TRY',
    'USD',
    'ZAR',
];
